import * as React from "react";
import { Box, Typography } from "@mui/material";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import { Team_0, Team_1, teamRgba } from "./GameStoryDialog_UPDATED";
import { ServePaddleIcon } from "./CondensedRallyTimeline"; // or wherever you put it

function getPointAwardedTeam_Calc(row: any): 0 | 1 | null {
  if (row?.pointAwardedTeam === 0 || row?.pointAwardedTeam === 1)
    return row.pointAwardedTeam;

  if (
    row?.scoredPoint === true &&
    (row?.servingTeam === 0 || row?.servingTeam === 1)
  ) {
    return row.servingTeam;
  }
  return null;
}

export function CondensedRallyTimeline_ContextOnly(props: {
  activeRally: KitchenArrivalRow | null;
  rows: KitchenArrivalRow[];

  /** Height (px) of the bars area. Keep small for “context strips”. */
  colW?: number;
  barsAreaHeightPx?: number;

  /** Show the legend row (“Team 0 / Team 1”). Default true. */
  showLegend?: boolean;

  /** Show rally index numbers above columns (tiny). Default false for max compact. */
  showRallyIndex?: boolean;

  /** Show winner star + serve paddle icon. Default true. */
  showWinnerAndServe?: boolean;
  showFirefightIcon?: boolean; // default true
  firefightRallySet: Set<number>;
  firefightIcon?: string; // default "🔥"
  onPickRally?: (rallyIndex: number) => void;
}) {
  const {
    rows,
    barsAreaHeightPx = 78,
    colW = 8,
    showLegend = true,
    showRallyIndex = false,
    showWinnerAndServe = true,
    activeRally,
    firefightRallySet,
    showFirefightIcon = true,
    firefightIcon = "🔥",
    onPickRally,
  } = props;

  const LABEL_H = 16;
  const GAP_H = 10;

  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  const barsH = Math.max(48, Math.min(180, Math.round(barsAreaHeightPx)));
  const barsRowH = Math.max(20, barsH - LABEL_H - GAP_H);

  const FOOTER_H = showWinnerAndServe ? 22 : 0;
  const colH = barsH + (showRallyIndex ? 14 : 0) + FOOTER_H;

  const GOLD_CAP_H = 0; //6;
  const goldCapBg =
    "linear-gradient(180deg, rgba(255,232,160,0.98) 0%, rgba(224,170,78,0.98) 55%, rgba(176,120,34,0.98) 100%)";
  const goldGlow = "0 0 10px rgba(224,170,78,0.55)";

  const perRally = React.useMemo(() => {
    const m = new Map<number, KitchenArrivalRow>();
    for (const r of rows) if (!m.has(r.rallyIndex)) m.set(r.rallyIndex, r);

    const base = Array.from(m.values()).sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );

    const items = base.map((r) => {
      const before = Array.isArray(r.scoreBefore) ? r.scoreBefore : [0, 0];
      const after = Array.isArray(r.scoreAfter) ? r.scoreAfter : before;

      const changed = r.scoredPoint === true;
      const displayScoreLabel = changed
        ? `${after[0]}-${after[1]}`
        : `${before[0]}-${before[1]}`;

      return { r, after, changed, displayScoreLabel };
    });

    const maxScoreEnd = items.reduce((mx, it) => {
      const a0 = Number(it.after?.[0] ?? 0);
      const a1 = Number(it.after?.[1] ?? 0);
      return Math.max(mx, a0, a1);
    }, 0);

    return { items, maxScoreEnd };
  }, [rows]);


  const scrollActiveIntoView = React.useCallback(() => {
    const ri = activeRally?.rallyIndex;
    if (ri == null) return;

    const scroller = scrollerRef.current;
    const el = itemRefs.current.get(ri);
    if (!scroller || !el) return;

    const sr = scroller.getBoundingClientRect();
    const er = el.getBoundingClientRect();

    // center-of-element minus center-of-viewport
    const delta = er.left + er.width / 2 - (sr.left + sr.width / 2);

    // already basically centered/visible
    if (Math.abs(delta) < 8) return;

    scroller.scrollBy({
      left: delta,
      behavior: "smooth",
    });
  }, [activeRally?.rallyIndex]);
  // React.useLayoutEffect(() => {
  //   // run after layout; double rAF helps when this opens inside a panel / portal
  //   let raf1 = 0;
  //   let raf2 = 0;

  //   raf1 = requestAnimationFrame(() => {
  //     raf2 = requestAnimationFrame(() => {
  //       scrollActiveIntoView();
  //     });
  //   });

  //   return () => {
  //     cancelAnimationFrame(raf1);
  //     cancelAnimationFrame(raf2);
  //   };
  // }, [scrollActiveIntoView, perRally.items.length, showLegend, showRallyIndex, showWinnerAndServe]);

  React.useLayoutEffect(() => {
    if (!activeRally?.rallyIndex) return;

    let cancelled = false;
    let tries = 0;

    const tick = () => {
      if (cancelled) return;

      const scroller = scrollerRef.current;
      const el = itemRefs.current.get(activeRally.rallyIndex);

      // Wait until both exist AND the scroller has a real width
      if (!scroller || !el || scroller.clientWidth === 0) {
        tries++;
        if (tries < 12) requestAnimationFrame(tick); // ~200ms worst case
        return;
      }

      // Now it's safe to scroll
      scrollActiveIntoView();
    };

    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
    };
  }, [activeRally?.rallyIndex, scrollActiveIntoView]);

  return (
    <Box
      ref={scrollerRef}
      sx={{
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        overflowX: "auto",
        whiteSpace: "nowrap",
        scrollBehavior: "smooth", // helps scrollTo feel nice
      }}
    >
      {showLegend && (
        <Box sx={{ px: 1, pt: 0.75, pb: 0.4 }}>
          <Box
            sx={{
              width: 20,
              height: 8,
              borderRadius: "2px",
              background: teamRgba(Team_0, 0.7),
              display: "inline-block",
              mr: "6px",
              verticalAlign: "middle",
            }}
          />
          <Box sx={{ display: "inline-block", opacity: 0.9, fontSize: 12 }}>
            Team 0
          </Box>

          <Box sx={{ display: "inline-block", width: 14 }} />

          <Box
            sx={{
              width: 20,
              height: 8,
              borderRadius: "2px",
              background: teamRgba(Team_1, 0.65),
              display: "inline-block",
              mr: "6px",
              verticalAlign: "middle",
            }}
          />
          <Box sx={{ display: "inline-block", opacity: 0.9, fontSize: 12 }}>
            Team 1
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "inline-flex",
          gap: 0.6,
          alignItems: "flex-end",
          px: 0,
          pb: "18px",
          pt: "6px",
        }}
      >
        {perRally.items.map(({ r, after, changed, displayScoreLabel }) => {
          const hasFirefight =
            props.showFirefightIcon !== false &&
            props.firefightRallySet?.has(r.rallyIndex);

            // console.error("hasFirefight", {
            //   rallyIndex: r.rallyIndex,
            //   showFirefightIcon: props.showFirefightIcon,
            //   firefightRallySet: props.firefightRallySet,
            //   hasFirefight: hasFirefight,
            // });

          const isActive = activeRally?.rallyIndex === r.rallyIndex;

          const max = Math.max(1, perRally.maxScoreEnd || 1);
          const t0 = Number(after?.[0] ?? 0);
          const t1 = Number(after?.[1] ?? 0);

          const h0 = Math.max(2, Math.round((t0 / max) * barsRowH));
          const h1 = Math.max(2, Math.round((t1 / max) * barsRowH));
          const tallest = Math.max(h0, h1);

          const labelTopPx = barsH - tallest - 14;
          const labelTop = Math.max(0, labelTopPx);

          const hasPoint = r.scoredPoint === true;
          const pointAwardedTeam = getPointAwardedTeam_Calc(r);

          const highlight0 = hasPoint && pointAwardedTeam === 0;
          const highlight1 = hasPoint && pointAwardedTeam === 1;

          const winnerColor =
            r.winnerTeam == null
              ? "rgba(255,255,255,0.18)"
              : r.winnerTeam === 0
                ? teamRgba(Team_0, 0.95)
                : teamRgba(Team_1, 0.95);

          const serveColor =
            r.servingTeam == null
              ? "rgba(255,255,255,0.12)"
              : r.servingTeam === 0
                ? teamRgba(Team_0, 1)
                : teamRgba(Team_1, 1);

          return (
            <Box
              key={`ctx-rally-${r.rallyIndex}`}
              //               ref={(el: HTMLDivElement | null) => {
              //   if (el) itemRefs.current.set(r.rallyIndex, el);
              //   else itemRefs.current.delete(r.rallyIndex);
              // }}
              ref={(el: HTMLDivElement | null) => {
                if (el) {
                  itemRefs.current.set(r.rallyIndex, el);

                  // if this is the active one, scroll right now
                  if (activeRally?.rallyIndex === r.rallyIndex) {
                    requestAnimationFrame(() => scrollActiveIntoView());
                  }
                } else {
                  itemRefs.current.delete(r.rallyIndex);
                }
              }}
              onClick={
                onPickRally ? () => onPickRally(r.rallyIndex) : undefined
              }
              sx={{
                width: colW || 8,
                height: colH,
                borderRadius: 1.2,
                // border: "1px solid rgba(255,255,255,0.14)",
                bgcolor:
                  r.servingTeam === 0 || r.servingTeam === 1
                    ? teamRgba(r.servingTeam, 0.08)
                    : "rgba(255,255,255,0.02)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                overflow: "visible",
                px: "2px",
                py: "4px",
                cursor: onPickRally ? "pointer" : "default",

                // transition:
                //   "transform 140ms ease, box-shadow 140ms ease, border 140ms ease, background 140ms ease",
                // border: isActive
                //   ? "1px solid rgba(0,0,0,0.85)"
                //   : "1px solid rgba(255,255,255,0.14)",

                // boxShadow: isActive
                //   ? "0 0 0 3px rgba(80,160,255,0.18)"
                //   : "none",
                // transform: isActive ? "translateY(-2px) scale(1.05)" : "none",


border: isActive
  ? "2px solid rgba(255,232,125,0.95)"
  : "1px solid rgba(255,255,255,0.14)",

boxShadow: isActive
  ? "0 0 0 2px rgba(255,232,125,0.22), 0 0 14px rgba(255,232,125,0.28)"
  : "none",

background: isActive
  ? "linear-gradient(180deg, rgba(255,232,125,0.12) 0%, rgba(255,232,125,0.04) 100%)"
  : undefined,

transform: isActive ? "translateY(-3px) scale(1.08)" : "none",
zIndex: isActive ? 2 : 1,

              }}
            >
              {showRallyIndex && (
                <Typography
                  sx={{
                    fontSize: 9,
                    opacity: 0.75,
                    fontWeight: 900,
                    lineHeight: 1,
                    mb: 0.25,
                    userSelect: "none",
                  }}
                >
                  {r.rallyIndex + 1}
                </Typography>
              )}

              <Box
                sx={{
                  height: barsH,
                  width: "100%",
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                {hasFirefight ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: -2,
                      top: 2,
                      fontSize: 10,
                      lineHeight: 1,
                      opacity: 0.9,
                      pointerEvents: "none",
                      textShadow: "0 1px 2px rgba(0,0,0,0.65)", // optional, helps on light/dark
                    }}
                  >
                    {props.firefightIcon ?? "🔥"}
                  </Box>
                ) : null}
                {/* score label only if point happened (same rule as original) */}
                {hasPoint && (
                  <Typography
                    sx={{
                      position: "absolute",
                      top: `${labelTop}px`,
                      left: 0,
                      right: 0,
                      textAlign: "center",
                      fontSize: 9,
                      fontWeight: 900,
                      opacity: 0.92,
                      lineHeight: 1,
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    {displayScoreLabel}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    gap: 0.35,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 5,
                      height: h0,
                      borderRadius: 1,
                      bgcolor: teamRgba(Team_0, 0.85),
                      opacity: hasPoint ? (highlight0 ? 1 : 0.7) : 0.2,
                      position: "relative",
                      ...(highlight0
                        ? {
                            "&:before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: `${GOLD_CAP_H}px`,
                              background: goldCapBg,
                              boxShadow: goldGlow,
                            },
                          }
                        : null),
                    }}
                  />
                  <Box
                    sx={{
                      width: 5,
                      height: h1,
                      borderRadius: 1,
                      bgcolor: teamRgba(Team_1, 1),
                      opacity: hasPoint ? (highlight1 ? 1 : 0.7) : 0.2,
                      position: "relative",
                      ...(highlight1
                        ? {
                            "&:before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: `${GOLD_CAP_H}px`,
                              background: goldCapBg,
                              boxShadow: goldGlow,
                            },
                          }
                        : null),
                    }}
                  />
                </Box>
              </Box>

              {showWinnerAndServe && (
                <Box
                  sx={{
                    mt: 0.35,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.2,
                    userSelect: "none",
                  }}
                >
                  {/* winner star */}
                  <Typography
                    sx={{
                      fontSize: 10,
                      lineHeight: 1,
                      fontWeight: 900,
                      color: `${winnerColor} !important`,
                      opacity: r.winnerTeam == null ? 0.35 : 1,
                      textShadow: "0 0 1px rgba(0,0,0,0.35)",
                    }}
                  >
                    ★
                  </Typography>

                  {/* serve paddle */}
                  <ServePaddleIcon
                    color={serveColor}
                    dim={r.servingTeam == null}
                    width={14}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
